import { DayMode, RoutineCategory, TaskStatus } from './enums';
import type { DailyStep } from './models';

export interface ModeStepOverride {
  readonly title: string;
  readonly estimatedDurationMinutes: number;
  readonly description?: string;
}

const SPORT_MODE_VARIANTS: Partial<Record<DayMode, ModeStepOverride>> = {
  [DayMode.busy]: {
    title: 'Séance de sport courte',
    estimatedDurationMinutes: 25,
    description:
      'Séance simplifiée pour maintenir le mouvement. Le contenu détaillé sera ajouté dans L7.',
  },
  [DayMode.travel]: {
    title: 'Mouvement en voyage',
    estimatedDurationMinutes: 25,
    description: 'Marche, mobilité ou circuit sans équipement.',
  },
  [DayMode.recovery]: {
    title: 'Récupération active',
    estimatedDurationMinutes: 25,
    description: 'Marche douce, mobilité et respiration. Aucune intensité élevée.',
  },
  [DayMode.difficult]: {
    title: 'Bouger dix minutes',
    estimatedDurationMinutes: 10,
  },
};

const EVENING_REVIEW_MODE_VARIANTS: Partial<Record<DayMode, ModeStepOverride>> = {
  [DayMode.busy]: {
    title: 'Bilan raccourci',
    estimatedDurationMinutes: 5,
  },
  [DayMode.difficult]: {
    title: 'Bilan court',
    estimatedDurationMinutes: 3,
  },
};

function overrideForCategory(
  category: DailyStep['category'],
  mode: DayMode,
): ModeStepOverride | undefined {
  if (category === RoutineCategory.sport) return SPORT_MODE_VARIANTS[mode];
  if (category === RoutineCategory.journal) return EVENING_REVIEW_MODE_VARIANTS[mode];
  return undefined;
}

/**
 * Rewrites the sport and evening-review snapshots for the day's mode. A step already in motion
 * (not `pending`) keeps its original title so history is never silently relabeled mid-action.
 */
export function applyModeVariant(step: DailyStep, mode: DayMode): DailyStep {
  if (step.status !== TaskStatus.pending) return step;
  const override = overrideForCategory(step.category, mode);
  if (!override) return step;
  return {
    ...step,
    title: override.title,
    estimatedDurationMinutes: override.estimatedDurationMinutes,
    ...(override.description === undefined ? {} : { description: override.description }),
  };
}

/**
 * Pure per-step predicate for AC-L0-MODE-02..07. A step generated only for specific modes
 * (`modes`) is excluded outside them; otherwise normal/travel/recovery/elevation/transition keep
 * the full routine (their exigence bears on depth, not on trimming the day), busy/moment-decisif/
 * imprevu keep only mandatory and essential-marked steps (a tightened focus), and difficult keeps
 * the sport slot, the evening-review slot, the difficult-only extras and essential-marked steps.
 */
export function isStepActiveForMode(step: DailyStep, mode: DayMode): boolean {
  if (step.modes && !step.modes.includes(mode)) return false;
  if (
    mode === DayMode.normal ||
    mode === DayMode.travel ||
    mode === DayMode.recovery ||
    mode === DayMode.elevation ||
    mode === DayMode.transition
  ) {
    return true;
  }
  if (mode === DayMode.busy || mode === DayMode.momentDecisif || mode === DayMode.imprevu) {
    return step.required || step.essential === true;
  }
  return (
    (step.modes?.includes(DayMode.difficult) ?? false) ||
    step.category === RoutineCategory.sport ||
    step.category === RoutineCategory.journal ||
    step.essential === true
  );
}

/**
 * Active-set derivation used for the progress denominator. Difficult mode additionally caps
 * essential-only inclusion to a single mission (AC-L0-MODE-06 says "une mission essentielle",
 * singular), chosen deterministically by the lowest display order.
 */
export function selectActiveSteps(
  steps: readonly DailyStep[],
  mode: DayMode,
): readonly DailyStep[] {
  const candidates = steps.filter((step) => isStepActiveForMode(step, mode));
  if (mode !== DayMode.difficult) return candidates;

  const fixed = candidates.filter(
    (step) =>
      step.category === RoutineCategory.sport ||
      step.category === RoutineCategory.journal ||
      step.modes?.includes(DayMode.difficult),
  );
  const fixedIds = new Set(fixed.map((step) => step.id));
  const essentialCandidates = candidates
    .filter((step) => !fixedIds.has(step.id))
    .slice()
    .sort((first, second) => first.order - second.order);
  const chosenEssential = essentialCandidates[0];
  return chosenEssential ? [...fixed, chosenEssential] : fixed;
}
